import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search, Heart, LogIn, Smartphone, X, ChevronUp, Clock } from 'lucide-react';
import config from '../config';
import '../styles/HomePage.css';
import '../styles/HomePageSearch.css';
import DataSourcesModal from '../components/DataSourcesModal';

// ─── Constants ────────────────────────────────────────────────────────────────
const API = config.API_URL || '/api';

// Groups that should never appear in feeds or community lists
const EXCLUDED_GROUP_IDS = new Set([
  'b784ca63-a788-4551-bbb7-cd8cb4469d29',
  'cde80c0e-75fd-4785-a0be-879afb0f5bc0',
  '0c0ebf95-6029-4951-8d8d-becf3f26e0ab',
]);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtItem = (c) => {
  const rawDate = c.computed_date || c.date || null;
  let dateStr = null;
  if (rawDate) {
    // Strip any existing time/timezone so we always parse a clean local date
    const dateOnly = String(rawDate).slice(0, 10); // 'YYYY-MM-DD'
    const d = new Date(dateOnly + 'T00:00:00');
    if (!isNaN(d.getTime())) {
      dateStr = d.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
      });
    }
  } else if (c.is_recurring) {
    const pattern = (c.recurrence_pattern || c.recurrence_day || '').toLowerCase();
    const DAYS = ['sunday','monday','tuesday','wednesday','thursday','friday','shabbat'];
    const found = DAYS.find(d => pattern.includes(d));
    if (/daily/.test(pattern)) dateStr = 'Daily';
    else if (/weekday|mon.*fri/.test(pattern)) dateStr = 'Weekdays';
    else if (found) dateStr = found.charAt(0).toUpperCase() + found.slice(1) + 's';
  }

  let timeStr = null;
  const t = c.start_time;
  if (t) {
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    timeStr = `${hr}:${String(m).padStart(2, '0')} ${ampm}`;
  }

  return { dateStr, timeStr };
};

const CARD_GRADIENTS = [
  'linear-gradient(145deg,#1e1b4b 0%,#4c1d95 100%)',
  'linear-gradient(145deg,#0c1445 0%,#1e3a8a 100%)',
  'linear-gradient(145deg,#064e3b 0%,#065f46 100%)',
  'linear-gradient(145deg,#1c1917 0%,#44403c 100%)',
  'linear-gradient(145deg,#4a044e 0%,#701a75 100%)',
  'linear-gradient(145deg,#172554 0%,#1e40af 100%)',
  'linear-gradient(145deg,#450a0a 0%,#991b1b 100%)',
  'linear-gradient(145deg,#14532d 0%,#166534 100%)',
];

const gradientFor = (id = '') => {
  const n = String(id).split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return CARD_GRADIENTS[n % CARD_GRADIENTS.length];
};

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="none"
    stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <path d="M16 2v4M8 2v4M3 10h18"/>
    <circle cx="8" cy="15" r="1" fill="rgba(255,255,255,0.45)" stroke="none"/>
    <circle cx="12" cy="15" r="1" fill="rgba(255,255,255,0.45)" stroke="none"/>
    <circle cx="16" cy="15" r="1" fill="rgba(255,255,255,0.45)" stroke="none"/>
  </svg>
);

const initials = (name = '') =>
  name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

// ─── Module-level helpers ─────────────────────────────────────────────────────

const TABS = [
  { id: 'all',      label: 'All'                 },
  { id: 'classes',  label: 'Classes'             },
  { id: 'events',   label: 'Events'              },
  { id: 'minyanim', label: 'Minyanim (Services)' },
];

// Check if a prayer-times object from the API has any actual times
const hasPtData = (pt) =>
  !!pt && Object.values(pt).some(day =>
    typeof day === 'object' &&
    Object.entries(day).some(([k, v]) => k !== 'custom' && Array.isArray(v) && v.length > 0)
  );

// Check if an agenda object has any items
const hasAgData = (ag) =>
  !!ag && Object.values(ag).some(d => Array.isArray(d) && d.length > 0);

// ─── Discovery chips — real states + top cities from our DB ─────────────────
// FL and CA have the most communities; cities ranked by group density.
const DISCOVER_CHIPS = [
  { label: 'Florida',         type: 'state', abbr: 'FL' },
  { label: 'California',      type: 'state', abbr: 'CA' },
  { label: 'New York',        type: 'state', abbr: 'NY' },
  { label: 'Miami',           type: 'city' },
  { label: 'Los Angeles',     type: 'city' },
  { label: 'Brooklyn',        type: 'city' },
  { label: 'Fort Lauderdale', type: 'city' },
  { label: 'Boca Raton',      type: 'city' },
  { label: 'Chicago',         type: 'city' },
  { label: 'Memphis',         type: 'city' },
  { label: 'Austin',          type: 'city' },
];

// Client-side enrich+sort (mirrors server enrichAndSort)
const clientEnrichAndSort = (items, type) => {
  const now = new Date().toISOString().split('T')[0];
  const todayDow = new Date().getDay();
  const DOW_MAP = { sunday:0, monday:1, tuesday:2, wednesday:3, thursday:4, friday:5, saturday:6, shabbat:6 };
  const nextOcc = (pattern) => {
    if (!pattern) return null;
    const p = String(pattern).toLowerCase();
    let targetDow = /daily/i.test(p) ? todayDow : null;
    if (targetDow === null) {
      for (const [name, dow] of Object.entries(DOW_MAP)) {
        if (p.includes(name)) { targetDow = dow; break; }
      }
    }
    if (targetDow === null) return null;
    const d = new Date();
    d.setDate(d.getDate() + (targetDow - todayDow + 7) % 7);
    return d.toISOString().split('T')[0];
  };
  return items
    .map(item => {
      let computed_date = null;
      if (item.is_recurring) {
        computed_date = nextOcc(item.recurrence_pattern || item.recurrence_day || '');
        if (!computed_date && item.date) {
          const d = String(item.date).slice(0, 10);
          if (d >= now) computed_date = d;
        }
      } else {
        const d = item.date ? String(item.date).slice(0, 10) : null;
        computed_date = d && d >= now ? d : null;
      }
      return { ...item, computed_date, _type: type };
    })
    .filter(item => item.is_recurring || item.computed_date)
    .sort((a, b) => {
      if (a.computed_date && b.computed_date) return a.computed_date.localeCompare(b.computed_date);
      if (a.computed_date) return -1;
      if (b.computed_date) return 1;
      return 0;
    });
};

// Client-side formatter for group-selected view — keeps ALL items, sorts upcoming/recurring first
// (Unlike clientEnrichAndSort which filters out past non-recurring items)
const clientFormatGroup = (items, type) => {
  const now = new Date().toISOString().split('T')[0];
  const todayDow = new Date().getDay();
  const DOW_MAP = { sunday:0, monday:1, tuesday:2, wednesday:3, thursday:4, friday:5, saturday:6, shabbat:6 };
  const nextOcc = (pattern) => {
    if (!pattern) return null;
    const p = String(pattern).toLowerCase();
    let targetDow = /daily/i.test(p) ? todayDow : null;
    if (targetDow === null) {
      for (const [name, dow] of Object.entries(DOW_MAP)) {
        if (p.includes(name)) { targetDow = dow; break; }
      }
    }
    if (targetDow === null) return null;
    const d = new Date();
    d.setDate(d.getDate() + (targetDow - todayDow + 7) % 7);
    return d.toISOString().split('T')[0];
  };
  return items
    .map(item => {
      let computed_date = null;
      if (item.is_recurring) {
        computed_date = nextOcc(item.recurrence_pattern || item.recurrence_day || '');
      }
      // Always capture the raw date as fallback (even if in the past — for display)
      if (!computed_date && item.date) {
        computed_date = String(item.date).slice(0, 10);
      }
      return { ...item, computed_date, _type: type };
    })
    .sort((a, b) => {
      // Upcoming or recurring items sort above past items
      const aUp = a.is_recurring || (a.computed_date && a.computed_date >= now);
      const bUp = b.is_recurring || (b.computed_date && b.computed_date >= now);
      if (aUp && !bUp) return -1;
      if (!aUp && bUp) return 1;
      // Within each group sort by date ascending (soonest first)
      if (a.computed_date && b.computed_date) return a.computed_date.localeCompare(b.computed_date);
      if (a.computed_date) return -1;
      if (b.computed_date) return 1;
      return (a.title || '').localeCompare(b.title || '');
    });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CommunityCircle = ({ g, onClick, selected, elRef }) => (
  <li ref={elRef} className={`hp-circle${selected ? ' hp-circle--selected' : ''}`} onClick={onClick}>
    <div className="hp-circle__avatar">
      {g.logo_url
        ? <img src={g.logo_url} alt={g.name} className="hp-circle__img" loading="lazy" />
        : <div className="hp-circle__init" style={{ background: gradientFor(g.id) }}>{initials(g.name)}</div>}
    </div>
    <p className="hp-circle__name">{g.name}</p>
  </li>
);

// ─── ContentCard — rich media card ──────────────────────────────────────────
const ContentCard = ({ item, onClick }) => {
  const { dateStr, timeStr } = fmtItem(item);
  const group     = item.group || {};
  const isEvent   = item._type === 'event';
  const gradient  = gradientFor(item.id);
  const schedText = timeStr || item.schedule || '';
  const whenParts = [dateStr, schedText].filter(Boolean);
  const typeLabel = isEvent ? 'Event' : item.is_recurring ? 'Weekly' : 'Class';

  return (
    <li className={`dc-card${isEvent ? ' dc-card--ev' : ' dc-card--cl'}`} onClick={onClick}>
      {/* Background */}
      {item.cover_image_url
        ? <img src={item.cover_image_url} alt="" className="dc-card__bg dc-card__bg--img" loading="lazy" aria-hidden="true" />
        : <div className="dc-card__bg" style={{ background: gradient }} />}
      {/* Group logo — centered watermark */}
      {group.logo_url && (
        <img src={group.logo_url} alt="" className="dc-card__logo-wm" aria-hidden="true" loading="lazy" />
      )}
      {/* Gradient scrim — ensures text is always readable */}
      <div className="dc-card__scrim" />
      {/* Type chip — top-left */}
      <span className="dc-card__chip">{typeLabel}</span>
      {/* Globe — top-right: this is public info */}
      <span className="dc-card__public" aria-label="Public information">
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      </span>
      {/* Text block at bottom */}
      <div className="dc-card__foot">
        {group.name && <p className="dc-card__org">{group.name}</p>}
        <p className="dc-card__title">{item.title}</p>
        {whenParts.length > 0 && (
          <p className="dc-card__when">
            <svg viewBox="0 0 16 16" width="10" height="10" fill="none"
              stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <rect x="2" y="3" width="12" height="11" rx="1.5"/>
              <path d="M5 1v3M11 1v3M2 7h12"/>
            </svg>
            {whenParts.join(' · ')}
          </p>
        )}
      </div>
    </li>
  );
};

// ─── MinyanimPanel — prayer times display ─────────────────────────────────────
// Convert any time string ("HH:MM:SS", "H:MM AM", etc.) to minutes from midnight for sort
const timeToMinutes = (t) => {
  if (!t) return 9999;
  const s   = String(t).trim();
  const isPM = /pm/i.test(s);
  const isAM = /am/i.test(s);
  const clean = s.replace(/\s*(AM|PM)/gi, '').trim();
  const [hPart = '0', mPart = '0'] = clean.split(':');
  let h = parseInt(hPart, 10) || 0;
  const m = parseInt(mPart, 10) || 0;
  if (isPM && h < 12) h += 12;
  if (isAM && h === 12) h = 0;
  return h * 60 + m;
};

const fmtPT = (t) => {
  if (!t) return '';
  const s     = String(t).trim();
  // Capture any existing AM/PM BEFORE stripping, so we can use it to
  // normalize 12-hour inputs (e.g. "7:30 PM") into a correct 24-hour value.
  const isPM  = /pm/i.test(s);
  const isAM  = /am/i.test(s);
  const clean = s.replace(/\s*(AM|PM)/gi, '').trim();
  const parts = clean.split(':');
  let h24     = parseInt(parts[0], 10) || 0;
  const mm    = parts[1] ? String(parseInt(parts[1], 10) || 0).padStart(2, '0') : '00';
  // Apply 12→24 hour conversion when the original string contained an indicator
  if (isPM && h24 < 12) h24 += 12;   // "7:30 PM"  → h24 = 19
  if (isAM && h24 === 12) h24 = 0;   // "12:00 AM" → h24 = 0
  // Now h24 is authoritative — derive display values
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12  = h24 % 12 || 12;
  return `${h12}:${mm} ${ampm}`;
};

const PT_ICONS = { shacharit: '☀️', mincha: '🌅', maariv: '🌙' };

const MinyanimPanel = ({ prayerTimes, agenda }) => {
  const DAYS      = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Shabbat'];
  const todayDow  = new Date().getDay();
  const todayKey  = todayDow === 6 ? 'Shabbat' : DAYS[todayDow];
  const todayIdx  = DAYS.indexOf(todayKey);
  const orderedDays = [...DAYS.slice(todayIdx), ...DAYS.slice(0, todayIdx)];

  return (
    <div className="hp-pt">
      {orderedDays.map((day, dIdx) => {
        const dayData  = prayerTimes?.[day] || {};
        const agItems  = (agenda?.[day] || []);

        // Gather all prayer rows (standard + custom cards)
        const rows = [];
        Object.entries(dayData).forEach(([key, times]) => {
          if (key === 'custom') return;
          (times || []).forEach(t => rows.push({
            label: key.charAt(0).toUpperCase() + key.slice(1),
            time:  t.time,
            icon:  PT_ICONS[key.toLowerCase()] || '⏰',
          }));
        });
        const customs = dayData.custom ? Object.values(dayData.custom) : [];
        customs.forEach(card =>
          (card.times || []).forEach(t =>
            rows.push({ label: card.name, time: t.time, icon: '📌' })
          )
        );
        // Agenda items (recurring classes on schedule)
        agItems.forEach(ai => rows.push({ label: ai.title, time: ai.time, icon: '📋' }));
        rows.sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

        if (!rows.length) return null;

        // Date label for the header
        const d = new Date();
        d.setDate(d.getDate() + dIdx);
        const monthDay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const dayLabel = dIdx === 0 ? 'Today' : dIdx === 1 ? 'Tomorrow' : day;

        return (
          <div key={day} className={`hp-pt__day${dIdx === 0 ? ' hp-pt__day--today' : ''}`}>
            <div className="hp-pt__hdr">
              <span className="hp-pt__dlabel">{dayLabel}</span>
              <span className="hp-pt__ddate">{monthDay}</span>
            </div>
            {rows.map((row, i) => (
              <div key={i} className="hp-pt__row">
                <span className="hp-pt__icon">{row.icon}</span>
                <span className="hp-pt__rtype">{row.label}</span>
                <span className="hp-pt__rtime">{fmtPT(row.time)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ─── PrayerCard — Acts of Kindness card ─────────────────────────────────────
const INTENTION_GRADIENTS = {
  tehillim: 'linear-gradient(145deg,#1e1b4b 0%,#4c1d95 100%)',
  challah:  'linear-gradient(145deg,#78350f 0%,#d97706 100%)',
};

const PrayerCard = ({ pg, onClick }) => {
  const accentColor = '#6366f1';
  const gradient    = INTENTION_GRADIENTS.tehillim;

  // chapters_in_current_book: unique chapters completed in the current sefer cycle
  const goal                 = pg.book_completion_goal || 150;
  const chaptersInCurrentBook = pg.chapters_in_current_book || 0;
  const pct                  = Math.min(100, (chaptersInCurrentBook / goal) * 100);
  const progressLabel        = `${Math.max(0, goal - chaptersInCurrentBook)} chapters left`;

  // Title = group name or recipient name; subtitle = the intention text
  const title     = pg.group_name || pg.recipient_name || 'Prayer Circle';
  const intention = pg.intention_display_text || 'Tehillim';

  return (
    <li className="pk-card" onClick={onClick}>
      {/* Colored top band */}
      <div className="pk-card__top"
        style={{ background: pg.card_image_url ? undefined : gradient }}>
        {pg.card_image_url && (
          <img src={pg.card_image_url} alt="" className="pk-card__img" loading="lazy" aria-hidden="true" />
        )}
        <div className="pk-card__top-scrim" />
        <span className="pk-card__type-pill">Tehillim</span>
      </div>

      {/* Text body */}
      <div className="pk-card__body">
        <p className="pk-card__title-main">{title}</p>
        <p className="pk-card__intention">{intention}</p>

        {/* Progress bar */}
        <div className="pk-card__progress-wrap">
          <div className="pk-card__progress-track">
            <div className="pk-card__progress-fill"
              style={{ width: `${pct}%`, background: accentColor }} />
          </div>
          <span className="pk-card__progress-label">{progressLabel}</span>
        </div>
      </div>
    </li>
  );
};

// ─── LocationSheet — smooth bottom sheet for setting city ───────────────────
const LocationSheet = ({ currentCity, onApply, onClose }) => {
  const [draft, setDraft] = useState(currentCity || '');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const inputRef = useRef(null);

  // Trap escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Auto-focus the text input after a short delay (so animation completes first)
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, []);

  const handleGps = () => {
    if (!navigator.geolocation) { setGpsError('Location not supported by this browser.'); return; }
    setGpsLoading(true);
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const r = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          if (r.ok) {
            const d = await r.json();
            const resolved = d.city || d.locality || d.principalSubdivision || '';
            if (resolved) { onApply(resolved, { lat: latitude, lng: longitude }); return; }
          }
        } catch { /* fallback */ }
        setGpsError('Could not detect city. Enter it manually below.');
        setGpsLoading(false);
      },
      () => {
        setGpsError('Location access denied. Enter your city below.');
        setGpsLoading(false);
      },
      { timeout: 6000 }
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const c = draft.trim();
    if (c) onApply(c, null);
  };

  return (
    <div className="lsh-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lsh" role="dialog" aria-modal="true" aria-label="Set your location">
        <div className="lsh__handle" />
        <button className="lsh__close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <h2 className="lsh__title">What city are you in?</h2>
        <p className="lsh__sub">We'll show you what's happening near you.</p>

        {/* GPS primary action */}
        <button
          className={`lsh__gps${gpsLoading ? ' lsh__gps--loading' : ''}`}
          onClick={handleGps}
          disabled={gpsLoading}
        >
          {gpsLoading ? (
            <svg className="lsh__spin" viewBox="0 0 24 24" width="18" height="18" fill="none"
              stroke="currentColor" strokeWidth="2.5">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
              <path d="M12 5a7 7 0 0 1 7 7 7 7 0 0 1-7 7 7 7 0 0 1-7-7 7 7 0 0 1 7-7z"/>
            </svg>
          )}
          {gpsLoading ? 'Detecting location…' : 'Use my current location'}
        </button>

        {gpsError && <p className="lsh__err">{gpsError}</p>}

        <div className="lsh__divider"><span>or enter city</span></div>

        {/* Manual text input */}
        <form onSubmit={handleSubmit} className="lsh__form">
          <input
            ref={inputRef}
            className="lsh__input"
            type="text"
            placeholder="e.g. Brooklyn, Miami, Los Angeles…"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <button type="submit" className="lsh__submit" disabled={!draft.trim()}>
            Set location
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── Minyan Preview — module-scope helpers ────────────────────────────────────
const _MP = {
  shacharit: { label: 'Shacharit', color: '#f59e0b' },
  mincha:    { label: 'Mincha',    color: '#0ea5e9' },
  maariv:    { label: 'Maariv',    color: '#6366f1' },
};
const mpCfg = (t) => _MP[t] || { label: 'Prayer', color: '#9ca3af' };

const mpFmt = (t) => {
  if (!t) return '';
  const s = t.trim();
  let h24, mm;
  if (/^\d{2}:\d{2}$/.test(s)) { [h24, mm] = s.split(':').map(Number); }
  else {
    const m = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!m) return t;
    h24 = parseInt(m[1]); mm = parseInt(m[2]);
    if (m[3].toLowerCase() === 'pm' && h24 !== 12) h24 += 12;
    if (m[3].toLowerCase() === 'am' && h24 === 12) h24 = 0;
  }
  const period = h24 >= 12 ? 'PM' : 'AM';
  const dh = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${dh}:${String(mm).padStart(2, '0')} ${period}`;
};

const mpNow  = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };
const mpMins = (t) => {
  if (!t) return null;
  const s = t.trim();
  let h, m;
  if (/^\d{2}:\d{2}$/.test(s)) { [h, m] = s.split(':').map(Number); }
  else {
    const mt = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    if (!mt) return null;
    h = parseInt(mt[1]); m = parseInt(mt[2]);
    if (mt[3].toLowerCase() === 'pm' && h !== 12) h += 12;
    if (mt[3].toLowerCase() === 'am' && h === 12) h = 0;
  }
  return h * 60 + m;
};
const mpStatus = (t) => {
  const mins = mpMins(t);
  if (mins === null) return null;
  const diff = mins - mpNow();
  if (diff >= -20 && diff <= 0)  return { type: 'now',  label: 'Now' };
  if (diff > 0  && diff <= 15)   return { type: 'soon', label: `In ${diff}m` };
  if (diff > 15 && diff <= 90)   return { type: 'soon', label: `In ${Math.round(diff / 5) * 5}m` };
  return null;
};

const QUICK_CITIES = [
  { label: 'Miami',       lat: 25.7617, lng: -80.1918 },
  { label: 'New York',    lat: 40.7128, lng: -74.0060 },
  { label: 'Los Angeles', lat: 34.0522, lng: -118.2437 },
  { label: 'Baltimore',   lat: 39.2904, lng: -76.6122  },
  { label: 'Chicago',     lat: 41.8781, lng: -87.6298  },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const HomePage = () => {
  const navigate = useNavigate();

  const [showDsModal, setShowDsModal] = useState(false);

  const [city, setCity]             = useState('');
  const [cityResolved, setCityResolved] = useState(false);
  const [userLatLng, setUserLatLng] = useState(null);
  const [locLoading, setLocLoading] = useState(true);

  const [feed, setFeed]             = useState([]);
  const [feedTitle, setFeedTitle]   = useState('Happening Near You');
  const [feedTier,  setFeedTier]    = useState(0); // tier from server response (3 = global fallback)
  const [classLoading, setClassLoading] = useState(false);

  const [communities, setCommunities] = useState([]);
  const [commLoading, setCommLoading] = useState(true);

  const classScrollRef    = useRef(null);
  const commScrollRef     = useRef(null);
  const cardScrollRef     = useRef(null);
  const kindnessScrollRef = useRef(null);
  const circlesStripRef   = useRef(null);

  const selectedCircleRef = useRef(null);
  // Ref mirror of activeTab — always current inside async callbacks
  const activeTabRef   = useRef('all');
  const mpTimerRef     = useRef(null);

  // ── Community-filter state ─────────────────────────────────────────────────
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab,     setActiveTab]     = useState('all');
  const [groupData,     setGroupData]     = useState(null);
  const [groupLoading,  setGroupLoading]  = useState(false);

  // Keep the ref mirror in sync so async callbacks always read the current tab
  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

  // ── Location sheet state ───────────────────────────────────────────────────
  const [locSheetOpen, setLocSheetOpen] = useState(false);

  // ── Acts of Kindness ──────────────────────────────────────────────────────
  const [prayerGroups,   setPrayerGroups]   = useState([]);
  const [prayerLoading,  setPrayerLoading]  = useState(true);

  // ── Minyan Preview ────────────────────────────────────────────────────────
  const [mpResults,   setMpResults]   = useState([]);
  const [mpLoading,   setMpLoading]   = useState(false);
  const [mpCityLabel, setMpCityLabel] = useState('');
  const [mpQuery,     setMpQuery]     = useState('');
  const [mpDropItems, setMpDropItems] = useState([]);
  const [mpShowDrop,  setMpShowDrop]  = useState(false);

  // ── Shabbat times widget ──────────────────────────────────────────────────
  const [shabbatData,    setShabbatData]    = useState(null);
  const [shabbatLoading, setShabbatLoading] = useState(false);

  // ── Location-based browsing ───────────────────────────────────────────────
  // 'states' → show state pills; 'cities' → show city circles; 'groups' → show org circles
  const [locationLevel,   setLocationLevel]   = useState('states');
  const [selectedState,   setSelectedState]   = useState(null);
  const [selectedCity,    setSelectedCity]    = useState(null);
  const [locationGroups,  setLocationGroups]  = useState([]);
  const [citiesInState,   setCitiesInState]   = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);

  // ── Geolocation ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) { setLocLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          setUserLatLng({ lat: latitude, lng: longitude });
          const r = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          if (r.ok) {
            const d = await r.json();
            const resolved = d.city || d.locality || d.principalSubdivision || '';
            if (resolved) { setCity(resolved); setCityResolved(true); }
          }
        } catch { /* fallback */ }
        setLocLoading(false);
      },
      () => setLocLoading(false),
      { timeout: 5000, maximumAge: 600000 }
    );
  }, []);

  // ── Feed ──────────────────────────────────────────────────────────────────
  const fetchFeed = useCallback(async (c, latLng, stateAbbr = '') => {
    setClassLoading(true);
    try {
      const params = new URLSearchParams({ city: c, limit: 20 });
      if (latLng) { params.set('lat', latLng.lat); params.set('lng', latLng.lng); }
      if (stateAbbr) params.set('state', stateAbbr);
      const r = await fetch(`${API}/public/groups/nearby-feed?${params}`);
      if (r.ok) {
        const d = await r.json();
        setFeed((d.data || []).filter(g => !EXCLUDED_GROUP_IDS.has(g.id)));
        if (d.title) setFeedTitle(d.title);
        if (d.tier)  setFeedTier(d.tier);
      }
    } catch { /* silent */ }
    setClassLoading(false);
  }, []);

  useEffect(() => {
    if (!locLoading) fetchFeed(city, userLatLng);
  }, [locLoading, city, userLatLng, fetchFeed]);

  // ── Communities ───────────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/public/groups/marquee-logos`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data?.length) setCommunities(d.data.filter(g => !EXCLUDED_GROUP_IDS.has(g.id))); })
      .catch(() => {})
      .finally(() => setCommLoading(false));
  }, []);

  // Update circles with city-relevant communities after location resolves
  useEffect(() => {
    if (!city || locLoading) return;
    fetch(`${API}/public/groups/city-search?q=${encodeURIComponent(city)}&limit=12`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data?.length) setCommunities(d.data.filter(g => !EXCLUDED_GROUP_IDS.has(g.id))); })
      .catch(() => {});
  }, [city, locLoading]);

  // ── Prayer groups feed ────────────────────────────────────────────────────
  useEffect(() => {
    fetch(`${API}/prayer-groups/public/recent?limit=20`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.data) setPrayerGroups(d.data); })
      .catch(() => {})
      .finally(() => setPrayerLoading(false));
  }, []);

  // ── Group select ──────────────────────────────────────────────────────────
  const handleSelectGroup = useCallback(async (g) => {
    if (selectedGroup?.id === g.id) {
      setSelectedGroup(null);
      setGroupData(null);
      setActiveTab('all');
      return;
    }
    // Read activeTab from the ref — always current, never a stale closure value
    const prevTab = activeTabRef.current;
    setSelectedGroup(g);
    setGroupData(null);
    setGroupLoading(true);
    try {
      const [evRes, clRes, ptRes, agRes] = await Promise.all([
        // No `upcoming=true` — recurring events often have a past base `date` and
        // would be wrongly excluded. Server now returns descending order when no
        // upcoming filter, so most recent/future events appear first within limit.
        fetch(`${API}/public/groups/${g.id}/events?limit=50`),
        fetch(`${API}/public/groups/${g.id}/classes?limit=50`),
        fetch(`${API}/public/groups/${g.id}/prayer-times`),
        fetch(`${API}/public/groups/${g.id}/agenda`),
      ]);
      const [evJson, clJson, ptJson, agJson] = await Promise.all([
        evRes.ok ? evRes.json() : { data: [] },
        clRes.ok ? clRes.json() : { data: [] },
        ptRes.ok ? ptRes.json() : { data: { prayerTimes: {} } },
        agRes.ok ? agRes.json() : { data: {} },
      ]);
      const data = {
        events:      clientFormatGroup(evJson.data || [], 'event'),
        classes:     clientFormatGroup(clJson.data || [], 'class'),
        prayerTimes: ptJson.data?.prayerTimes || {},
        agenda:      agJson.data || {},
      };
      setGroupData(data);
      // Restore the tab the user was on, if that tab has content in the new group.
      // Only fall back to a different tab when the restored tab would be empty.
      const hasContent = data.classes.length > 0 || data.events.length > 0;
      const hasPT      = hasPtData(data.prayerTimes) || hasAgData(data.agenda);
      if (prevTab === 'minyanim') {
        setActiveTab(hasPT ? 'minyanim' : hasContent ? 'all' : 'all');
      } else if (prevTab === 'classes') {
        setActiveTab(data.classes.length > 0 ? 'classes' : hasContent ? 'all' : hasPT ? 'minyanim' : 'all');
      } else if (prevTab === 'events') {
        setActiveTab(data.events.length > 0 ? 'events' : hasContent ? 'all' : hasPT ? 'minyanim' : 'all');
      } else {
        // 'all' — keep it, or fall to minyanim if truly no content
        setActiveTab(hasContent ? 'all' : hasPT ? 'minyanim' : 'all');
      }
    } catch { /* silent */ }
    setGroupLoading(false);
  }, [selectedGroup]);

  // ── Location-based browsing handlers ─────────────────────────────────────
  const handleSelectState = useCallback(async (state) => {
    if (selectedState?.abbr === state.abbr) {
      // Toggle off → restore original geo/city feed
      setSelectedState(null);
      setSelectedCity(null);
      setLocationLevel('states');
      setLocationGroups([]);
      setCitiesInState([]);
      setSelectedGroup(null);
      setGroupData(null);
      setActiveTab('all');
      fetchFeed(city, userLatLng);
      return;
    }
    setSelectedState(state);
    setSelectedCity(null);
    setLocationLevel('cities');
    setLocationLoading(true);
    setSelectedGroup(null);
    setGroupData(null);
    setActiveTab('all');
    // Immediately load state-wide events/classes feed (runs in parallel)
    fetchFeed('', null, state.abbr);
    try {
      const r = await fetch(`${API}/public/groups/city-search?state=${encodeURIComponent(state.abbr)}&limit=500`);
      if (r.ok) {
        const d = await r.json();
        const groups = (d.data || []).filter(g => !EXCLUDED_GROUP_IDS.has(g.id));
        setLocationGroups(groups);
        // Sort cities by group count descending
        const cityCount = {};
        groups.forEach(g => { if (g.city) cityCount[g.city] = (cityCount[g.city] || 0) + 1; });
        const cities = Object.entries(cityCount)
          .sort(([, a], [, b]) => b - a)
          .map(([c]) => c);
        setCitiesInState(cities);
      }
    } catch { /* silent */ }
    setLocationLoading(false);
  }, [selectedState, fetchFeed, city, userLatLng]);

  const handleSelectCity = useCallback((cityName) => {
    if (selectedCity === cityName) {
      // Toggle off → back to cities view, restore state-wide feed
      setSelectedCity(null);
      setLocationLevel('cities');
      setSelectedGroup(null);
      setGroupData(null);
      setActiveTab('all');
      if (selectedState) fetchFeed('', null, selectedState.abbr);
      return;
    }
    setSelectedCity(cityName);
    setLocationLevel('groups');
    setSelectedGroup(null);
    setGroupData(null);
    setActiveTab('all');
    // Pass the state abbreviation alongside city for a more precise feed
    const stateAbbr = selectedState?.abbr || '';
    fetchFeed(cityName, null, stateAbbr);
  }, [selectedCity, selectedState, fetchFeed]);

  const handleQuickCityChip = useCallback((cityName) => {
    // Jump directly to city-level feed, no state drill needed
    setSelectedCity(cityName);
    setSelectedState(null);
    setLocationGroups([]);
    setCitiesInState([]);
    setLocationLevel('states');
    setSelectedGroup(null); setGroupData(null); setActiveTab('all');
    fetchFeed(cityName, null, '');
  }, [fetchFeed]);

  const handleLocationApply = (newCity, latLng) => {
    setCity(newCity);
    setCityResolved(true);
    setLocSheetOpen(false);
    setSelectedGroup(null);
    setGroupData(null);
    setActiveTab('all');
    if (latLng) setUserLatLng(latLng);
    fetchFeed(newCity, latLng || userLatLng);
  };

  const handleUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) { setLocSheetOpen(true); return; }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const latlng = { lat: latitude, lng: longitude };
          setUserLatLng(latlng);
          const r = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          if (r.ok) {
            const d = await r.json();
            const resolved = d.city || d.locality || d.principalSubdivision || '';
            if (resolved) { setCity(resolved); setCityResolved(true); fetchFeed(resolved, latlng); }
            else fetchFeed('', latlng);
          }
        } catch { /* fallback */ }
        setLocLoading(false);
      },
      () => { setLocLoading(false); setLocSheetOpen(true); },
      { timeout: 8000 }
    );
  }, [fetchFeed]);

  const handleClearLocation = useCallback(() => {
    setCity('');
    setCityResolved(false);
    setUserLatLng(null);
    // Reset state/city browse too
    setSelectedState(null); setSelectedCity(null);
    setLocationLevel('states'); setLocationGroups([]); setCitiesInState([]);
    setSelectedGroup(null); setGroupData(null); setActiveTab('all');
    fetchFeed('', null);
  }, [fetchFeed]);

  const openApp = () => {
    const ua = navigator.userAgent || '';
    const url = /android/i.test(ua)
      ? 'https://play.google.com/store/apps/details?id=us.yesod.app'
      : 'https://apps.apple.com/us/app/yesod/id6748472269';
    window.open(url, '_blank');
  };

  // ── Drag-scroll ───────────────────────────────────────────────────────────
  const addDragScroll = (ref) => {
    const el = ref.current;
    if (!el) return;
    let isDown = false, startX = 0, scrollLeft = 0;
    // Vertical wheel → horizontal scroll (desktop mouse wheel support)
    const onWheel = e => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // natural horizontal trackpad — pass through
      e.preventDefault();
      el.scrollLeft += e.deltaY * 1.2;
    };
    const onDown = e => {
      isDown = true;
      startX = e.pageX;
      scrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    };
    const onUp = () => {
      isDown = false;
      el.style.cursor = '';
      el.style.userSelect = '';
    };
    const onMove = e => {
      if (!isDown) return;
      e.preventDefault();
      el.scrollLeft = scrollLeft - (e.pageX - startX);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('mousedown', onDown);
    el.addEventListener('mouseleave', onUp);
    el.addEventListener('mouseup', onUp);
    el.addEventListener('mousemove', onMove);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('mousedown', onDown);
      el.removeEventListener('mouseleave', onUp);
      el.removeEventListener('mouseup', onUp);
      el.removeEventListener('mousemove', onMove);
    };
  };

  useEffect(() => addDragScroll(classScrollRef),    [feed]);
  useEffect(() => addDragScroll(commScrollRef),     [communities]);
  useEffect(() => addDragScroll(cardScrollRef),     [feed, groupData, activeTab]);
  useEffect(() => addDragScroll(kindnessScrollRef), [prayerGroups]);
  useEffect(() => addDragScroll(circlesStripRef),   [citiesInState, locationGroups, locationLevel]);

  // ── Minyan Preview callbacks ───────────────────────────────────────────────
  const mpFetch = useCallback(async (lat, lng, label) => {
    setMpLoading(true);
    setMpResults([]);
    setMpCityLabel(label);
    setMpShowDrop(false);
    try {
      const res  = await fetch(`${API}/public/minyanim?lat=${lat}&lng=${lng}`);
      const data = await res.json();
      setMpResults((data.minyanim || []).slice(0, 5));
    } catch { setMpResults([]); }
    finally  { setMpLoading(false); }
  }, []);

  const mpSearchCity = useCallback((q) => {
    setMpQuery(q);
    clearTimeout(mpTimerRef.current);
    if (!q.trim()) { setMpDropItems([]); setMpShowDrop(false); return; }
    mpTimerRef.current = setTimeout(async () => {
      try {
        const res  = await fetch(`${API}/public/groups/city-search?q=${encodeURIComponent(q.trim())}`);
        const data = await res.json();
        setMpDropItems(data.data || []);
        setMpShowDrop(true);
      } catch { setMpDropItems([]); }
    }, 280);
  }, []);

  const mpPickItem = useCallback((item) => {
    let lat, lng, label;
    if (item.latitude && item.longitude) {
      lat = item.latitude; lng = item.longitude;
      label = `${item.city || item.name}${item.state ? `, ${item.state}` : ''}`;
    } else if (item.shuls) {
      const w = (item.shuls || []).filter(g => g.latitude && g.longitude);
      if (!w.length) return;
      lat   = w.reduce((s, g) => s + parseFloat(g.latitude),  0) / w.length;
      lng   = w.reduce((s, g) => s + parseFloat(g.longitude), 0) / w.length;
      label = `${item.city}${item.state ? `, ${item.state}` : ''} area`;
    } else return;
    setMpQuery('');
    mpFetch(lat, lng, label);
  }, [mpFetch]);

  // Auto-populate preview when user's geolocation is resolved by the page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (userLatLng && !mpCityLabel) {
      mpFetch(userLatLng.lat, userLatLng.lng, city || 'Your location');
    }
  }, [userLatLng]); // intentionally omit mpCityLabel to only fire once

  // ── Shabbat times: fetch when location resolves ───────────────────────────
  useEffect(() => {
    if (locLoading || shabbatData) return; // wait for location; don't re-fetch
    if (!userLatLng && !city) return;      // no location — don't show times
    const fetchShabbat = async () => {
      setShabbatLoading(true);
      try {
        const params = new URLSearchParams();
        if (userLatLng) {
          params.set('lat', userLatLng.lat);
          params.set('lng', userLatLng.lng);
        } else {
          params.set('q', city);
        }
        const res  = await fetch(`${API}/public/shabbat-times?${params}`);
        const json = await res.json();
        if (json.success) setShabbatData(json);
      } catch { /* silent — widget is non-critical */ }
      finally { setShabbatLoading(false); }
    };
    fetchShabbat();
  }, [locLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll the selected circle into view whenever it changes
  useEffect(() => {
    if (selectedCircleRef.current) {
      selectedCircleRef.current.scrollIntoView({
        inline: 'nearest',
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [selectedGroup]);

  // ── Derived feed data ──────────────────────────────────────────────────────
  const groupClassesFmt = (groupData?.classes || []).map(c => ({ ...c, group: selectedGroup }));
  const groupEventsFmt  = (groupData?.events  || []).map(e => ({ ...e, group: selectedGroup }));

  // Groups for the city-level circles (derived, not state)
  const cityGroups = locationGroups.filter(g => g.city === selectedCity);

  const displayFeed    = selectedGroup
    ? [...groupClassesFmt, ...groupEventsFmt].sort((a, b) => {
        const now = new Date().toISOString().split('T')[0];
        const aUp = a.is_recurring || (a.computed_date && a.computed_date >= now);
        const bUp = b.is_recurring || (b.computed_date && b.computed_date >= now);
        if (aUp && !bUp) return -1;
        if (!aUp && bUp) return 1;
        if (a.computed_date && b.computed_date) return a.computed_date.localeCompare(b.computed_date);
        return 0;
      })
    : feed;
  const displayClasses = selectedGroup ? groupClassesFmt : feed.filter(i => i._type === 'class');
  const displayEvents  = selectedGroup ? groupEventsFmt  : feed.filter(i => i._type === 'event');

  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="hp">

      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <header className="hp-nav">
        <div className="hp-nav__inner">
          <a href="/" className="hp-nav__logo-wrap" aria-label="Yesod home">
            <img
              src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png"
              alt="Yesod"
              className="hp-nav__logo-img"
            />
          </a>
          {/* Desktop inline links */}
          <nav className="hp-nav__links">
            <button className="hp-nav__nav-link" onClick={() => navigate('/search')}>Communities</button>
            <button className="hp-nav__nav-link" onClick={() => navigate('/explore-prayers')}>Acts of Kindness</button>
            <button className="hp-nav__nav-link" onClick={() => navigate('/minyan')}>Find a Minyan</button>
            <button className="hp-nav__nav-link" onClick={() => navigate('/shabbat')}>Shabbat Times</button>
          </nav>
          <div className="hp-nav__actions">
            <button className="hp-nav__link hp-nav__link--desktop" onClick={() => navigate('/login')}>Sign in</button>
            <button className="hp-nav__cta hp-nav__cta--desktop" onClick={openApp}>Get the app</button>
            {/* Hamburger — mobile only */}
            <button
              className="hp-nav__burger"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={navOpen}
              onClick={() => setNavOpen(o => !o)}
            >
              <span className={`hp-nav__burger-icon${navOpen ? ' hp-nav__burger-icon--open' : ''}`}>
                <span /><span /><span />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* ── MOBILE DRAWER ────────────────────────────────────────────────── */}
      {navOpen && (
        <div className="hp-drawer-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
      )}
      <aside className={`hp-drawer${navOpen ? ' hp-drawer--open' : ''}`} aria-hidden={!navOpen}>
        <div className="hp-drawer__header">
          <img
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png"
            alt="Yesod"
            className="hp-drawer__logo"
          />
          <button className="hp-drawer__close" onClick={() => setNavOpen(false)} aria-label="Close menu">
            <X size={18} />
          </button>
        </div>
        <div className="hp-drawer__section-label">
          <span>Explore</span>
          <ChevronUp size={20} />
        </div>
        <div className="hp-drawer__top-divider" />
        <nav className="hp-drawer__nav">
          <button className="hp-drawer__item" onClick={() => { navigate('/search'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><Search size={20} /></span>
            Communities
          </button>
          <button className="hp-drawer__item" onClick={() => { navigate('/explore-prayers'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><Heart size={20} /></span>
            Chessed
          </button>
          <button className="hp-drawer__item" onClick={() => { navigate('/minyan'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><MapPin size={20} /></span>
            Find a Minyan
          </button>
          <button className="hp-drawer__item" onClick={() => { navigate('/shabbat'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><Clock size={20} /></span>
            Shabbat Times
          </button>
          <div className="hp-drawer__divider" />
          <button className="hp-drawer__item" onClick={() => { navigate('/login'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><LogIn size={20} /></span>
            Sign in
          </button>
        </nav>
        <div className="hp-drawer__footer">
          <button className="hp-drawer__cta" onClick={() => { openApp(); setNavOpen(false); }}>
            <Smartphone size={18} />
            Continue in App
          </button>
        </div>
      </aside>

      <main className="hp-main">

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="hp-hero">
          <div className="hp-hero__inner">

            {/* Left — copy + search */}
            <div className="hp-hero__text">
              <h1 className="hp-hero__heading">
                {cityResolved
                  ? 'Connect With Your Local Jewish Community'
                  : 'Explore Jewish Life Near You'}
              </h1>
              <p className="hp-hero__sub">
                {locLoading ? (
                  // Still detecting location
                  'Find classes, events, and prayer times in your community.'
                ) : cityResolved ? (
                  <>
                    Discover classes, events, and prayer times in{' '}
                    <button
                      className="hp-hero__city"
                      onClick={() => navigate(`/search?q=${encodeURIComponent(city)}`)}
                      aria-label={`Search near ${city}`}
                    >
                      {city}
                    </button>
                    {' '}and nearby.
                  </>
                ) : (
                  'Find classes, events, and prayer times in your community.'
                )}
              </p>
              <button
                className="hp-hero__search"
                onClick={() => navigate(`/search?q=${encodeURIComponent(city)}`)}
                aria-label="Search communities"
              >
                <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
                  <circle cx="9" cy="9" r="6" stroke="#9ca3af" strokeWidth="2"/>
                  <path d="M13.5 13.5l3 3" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="hp-hero__search-placeholder">Search communities, classes, events…</span>
                <span className="hp-hero__search-action">Search</span>
              </button>
            </div>

            {/* Right — phone mockup */}
            <div className="gl-hero__visual" aria-hidden="true">
              <div className="gl-phone-glow" />
              <div className="gl-phone" role="img" aria-label="App screen preview">
                <div className="gl-phone__notch" />
                <div className="gl-phone__bar">
                  <span>9:41</span><span>●●●</span>
                </div>
                <div className="gl-mk">
                  <div className="gl-mk__top">
                    <span className="gl-mk__wordmark">Yesod</span>
                    <div className="gl-mk__loc">
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      New York, NY
                    </div>
                    <div className="gl-mk__bell">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                      <span className="gl-mk__bell-badge">5</span>
                    </div>
                  </div>
                  <div className="gl-mk__sec-label">
                    <span>Happening Soon</span>
                    <span className="gl-mk__sec-all">See all</span>
                  </div>
                  <div className="gl-mk__hero">
                    <div className="gl-mk__hero-orb gl-mk__hero-orb--a" />
                    <div className="gl-mk__hero-orb gl-mk__hero-orb--b" />
                    <div className="gl-mk__hero-orb gl-mk__hero-orb--c" />
                    <div className="gl-mk__hero-top">
                      <span className="gl-mk__live-chip">
                        <span className="gl-mk__live-dot" />
                        Tonight
                      </span>
                      <div className="gl-mk__hero-org">
                        <div className="gl-mk__org-av">CN</div>
                        <span>Chabad of Naples</span>
                      </div>
                    </div>
                    <div className="gl-mk__hero-name">Shabbat<br/>Dinner</div>
                    <div className="gl-mk__hero-when">Fri, Mar 7 · 7:30 PM</div>
                    <div className="gl-mk__hero-foot">
                      <div className="gl-mk__hero-avs">
                        <div className="gl-mk__hav" style={{ background: 'linear-gradient(135deg,#f97316,#ef4444)' }} />
                        <div className="gl-mk__hav" style={{ background: 'linear-gradient(135deg,#8b5cf6,#6366f1)' }} />
                        <div className="gl-mk__hav" style={{ background: 'linear-gradient(135deg,#06b6d4,#0284c7)' }} />
                        <div className="gl-mk__hav gl-mk__hav--more">+29</div>
                      </div>
                      <span className="gl-mk__hero-count">32 attending</span>
                      <div className="gl-mk__hero-rsvp">RSVP →</div>
                    </div>
                  </div>
                  <div className="gl-mk__sec-label gl-mk__sec-label--pad">
                    <span>Also tonight</span>
                    <span className="gl-mk__sec-all">→</span>
                  </div>
                  <div className="gl-mk__feed-row">
                    <div className="gl-mk__feed-av" style={{ background: 'linear-gradient(135deg,#16a34a,#065f46)' }}>YI</div>
                    <div className="gl-mk__feed-info">
                      <strong>Mincha · Maariv</strong>
                      <span>Young Israel · 6:15 PM</span>
                    </div>
                    <span className="gl-mk__tag gl-mk__tag--green">Davening</span>
                  </div>
                  <div className="gl-mk__feed-row">
                    <div className="gl-mk__feed-av" style={{ background: 'linear-gradient(135deg,#1d4ed8,#4f46e5)' }}>JC</div>
                    <div className="gl-mk__feed-info">
                      <strong>Board Game Night</strong>
                      <span>JCC Manhattan · 7:00 PM</span>
                    </div>
                    <span className="gl-mk__tag gl-mk__tag--blue">Social</span>
                  </div>
                  <div className="gl-mk__feed-row">
                    <div className="gl-mk__feed-av" style={{ background: 'linear-gradient(135deg,#b45309,#92400e)' }}>BT</div>
                    <div className="gl-mk__feed-info">
                      <strong>Parsha Study Group</strong>
                      <span>Beis Torah · 8:00 PM</span>
                    </div>
                    <span className="gl-mk__tag gl-mk__tag--amber">Class</span>
                  </div>
                  <div className="gl-mk__tabbar">
                    <div className="gl-mk__titem gl-mk__titem--on">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                      <div className="gl-mk__tdot" />
                    </div>
                    <div className="gl-mk__titem">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    </div>
                    <div className="gl-mk__titem">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                    </div>
                    <div className="gl-mk__titem">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="gl-float gl-float--1">
                <div className="gl-float__dot gl-float__dot--green" />
                <div>
                  <strong>Shabbat Dinner</strong>
                  <span>Tonight · 12 going</span>
                </div>
              </div>
              <div className="gl-float gl-float--2">
                <div className="gl-float__dot gl-float__dot--blue" />
                <div>
                  <strong>3 events near you</strong>
                  <span>This weekend</span>
                </div>
              </div>
              <div className="gl-float gl-float--3">
                <div className="gl-float__dot gl-float__dot--amber" />
                <div>
                  <strong>Mincha starting soon</strong>
                  <span>In 10 minutes</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STATS ────────────────────────────────────────────────────── */}
        <div className="hp-stats">
          <div className="hp-stats__inner">
            <div className="hp-stats__item">
              <span className="hp-stats__num">200+</span>
              <span className="hp-stats__label">Communities</span>
            </div>
            <div className="hp-stats__divider" />
            <div className="hp-stats__item">
              <span className="hp-stats__num">500+</span>
              <span className="hp-stats__label">Events &amp; classes</span>
            </div>
            <div className="hp-stats__divider" />
            <div className="hp-stats__item">
              <span className="hp-stats__num">100+</span>
              <span className="hp-stats__label">Cities</span>
            </div>
          </div>
        </div>

        {/* ── DISCOVER ─────────────────────────────────────────────────── */}
        <section className="hp-discover">

          {/* Location row */}
          <div className="hp-loc">
            {/* Left — geo action */}
            {locLoading ? (
              <span className="hp-loc__skel hp-loc__skel--btn" />
            ) : cityResolved ? (
              <div className="hp-loc__near-chip">
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <button className="hp-loc__near-city" onClick={() => setLocSheetOpen(true)}
                  aria-label={`Change location from ${city}`}>
                  Near {city}
                </button>
                <button className="hp-loc__near-clear" onClick={handleClearLocation}
                  aria-label="Clear location">
                  <svg viewBox="0 0 24 24" width="9" height="9" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            ) : (
              <button className="hp-loc__geo-btn" onClick={handleUseMyLocation}>
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                </svg>
                Use my location
              </button>
            )}

            {/* Right — explore CTA */}
            <button className="hp-loc__explore" onClick={() => navigate('/search')}>
              Explore communities
              <svg viewBox="0 0 24 24" width="11" height="11" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>

          {/* Location sheet */}
          {locSheetOpen && (
            <LocationSheet
              currentCity={city}
              onApply={handleLocationApply}
              onClose={() => setLocSheetOpen(false)}
            />
          )}

          {/* ── Location browser: States → Cities → Groups ── */}

          {/* Breadcrumb when drilled into a state / city */}
          {(selectedState || selectedCity) && (
            <div className="hp-loc-breadcrumb">
              <button
                className="hp-loc-bc-item hp-loc-bc-item--link"
                onClick={() => {
                  setLocationLevel('states');
                  setSelectedState(null); setSelectedCity(null);
                  setLocationGroups([]); setCitiesInState([]);
                  setSelectedGroup(null); setGroupData(null);
                  setActiveTab('all');
                  fetchFeed(city, userLatLng);
                }}
              >All States</button>
              {selectedState && (
                <>
                  <span className="hp-loc-bc-sep">›</span>
                  <button
                    className={`hp-loc-bc-item${!selectedCity ? ' hp-loc-bc-item--current' : ' hp-loc-bc-item--link'}`}
                    onClick={() => {
                      if (selectedCity) {
                        setSelectedCity(null);
                        setLocationLevel('cities');
                        setSelectedGroup(null); setGroupData(null);
                        setActiveTab('all');
                        fetchFeed('', null, selectedState.abbr);
                      }
                    }}
                  >{selectedState.name}</button>
                </>
              )}
              {selectedCity && (
                <>
                  <span className="hp-loc-bc-sep">›</span>
                  <span className="hp-loc-bc-item hp-loc-bc-item--current">{selectedCity}</span>
                </>
              )}
            </div>
          )}

          {/* Level 0 — Unified discovery chips: top states + real cities from DB */}
          {locationLevel === 'states' && (
            <div className="hp-state-pills">
              {DISCOVER_CHIPS.map(chip =>
                chip.type === 'state' ? (
                  <button
                    key={chip.abbr}
                    className={`hp-state-pill hp-state-pill--state${selectedState?.abbr === chip.abbr ? ' hp-state-pill--active' : ''}`}
                    onClick={() => handleSelectState({ name: chip.label, abbr: chip.abbr })}
                  >{chip.label}</button>
                ) : (
                  <button
                    key={chip.label}
                    className={`hp-state-pill${selectedCity === chip.label && !selectedState ? ' hp-state-pill--active' : ''}`}
                    onClick={() => handleQuickCityChip(chip.label)}
                  >{chip.label}</button>
                )
              )}
            </div>
          )}

          {/* Level 1 — City circles */}
          {locationLevel === 'cities' && (
            locationLoading ? (
              <ul className="hp-circles-strip">
                {[...Array(6)].map((_, i) => (
                  <li key={i} className="hp-circle">
                    <div className="hp-circle__avatar hp-skeleton" />
                    <div className="hp-skeleton hp-skeleton--sm" style={{ width: 52, margin: '6px auto 0' }} />
                  </li>
                ))}
              </ul>
            ) : citiesInState.length === 0 ? (
              <p className="hp-loc-empty">No communities found in {selectedState?.name}.</p>
            ) : (
              <ul className="hp-circles-strip" ref={circlesStripRef}>
                {citiesInState.slice(0, 12).map(c => (
                  <li
                    key={c}
                    className={`hp-circle hp-circle--city${selectedCity === c ? ' hp-circle--selected' : ''}`}
                    onClick={() => handleSelectCity(c)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && handleSelectCity(c)}
                  >
                    <div className="hp-circle__avatar hp-circle__avatar--city">
                      <MapPin size={22} color="#fff" strokeWidth={2.5} aria-hidden="true" />
                    </div>
                    <p className="hp-circle__name">{c}</p>
                  </li>
                ))}
              </ul>
            )
          )}

          {/* Level 2 — Group circles (city selected) */}
          {locationLevel === 'groups' && (
            cityGroups.length === 0 ? (
              <p className="hp-loc-empty">No communities found in {selectedCity}.</p>
            ) : (
              <ul className="hp-circles-strip" ref={circlesStripRef}>
                {cityGroups.slice(0, 8).map(g => (
                  <CommunityCircle
                    key={g.id}
                    g={g}
                    selected={selectedGroup?.id === g.id}
                    elRef={selectedGroup?.id === g.id ? selectedCircleRef : null}
                    onClick={() => handleSelectGroup(g)}
                  />
                ))}
              </ul>
            )
          )}

          {/* Dynamic feed context label */}
          <div className="hp-feed-header">
            <p className="hp-feed-label">
              {selectedGroup
                ? selectedGroup.name
                : feedTier === 3 && (selectedState || selectedCity || cityResolved) && !classLoading && !groupLoading
                  ? `Nothing found for ${selectedCity || selectedState?.name || city} — here's what's happening in your region`
                  : selectedCity
                    ? (selectedState ? `${selectedCity}, ${selectedState.abbr}` : selectedCity)
                    : selectedState
                      ? `Happening in ${selectedState.name}`
                      : cityResolved
                        ? `Near ${city}`
                        : 'Discover events & classes'}
            </p>
            {(classLoading || groupLoading) && (
              <span className="hp-feed-loading-dot" />
            )}
          </div>

          {/* Combined feed — classes + events, no tabs */}
          <div className="hp-tab-panel">
            {(classLoading || groupLoading) ? (
              <ul className="dc-cards">
                {[...Array(4)].map((_, i) => (
                  <li key={i} className="dc-card dc-card--skel">
                    <div className="dc-card__bg" />
                    <div className="dc-card__scrim" />
                    <span className="dc-card__chip dc-card__chip--skel" />
                    <div className="dc-card__foot">
                      <div className="dc-skel-line" style={{ width: '55%', marginBottom: 6 }} />
                      <div className="dc-skel-line dc-skel-line--lg" />
                      <div className="dc-skel-line" style={{ width: '65%', marginTop: 8 }} />
                    </div>
                  </li>
                ))}
              </ul>
            ) : displayFeed.length > 0 ? (
              <ul className="dc-cards" ref={cardScrollRef}>
                {displayFeed.map(item => (
                  <ContentCard
                    key={`${item._type}-${item.id}`}
                    item={item}
                    onClick={() => {
                      const slug = item.group?.slug || item.group_id;
                      navigate(`/community/${slug}`);
                    }}
                  />
                ))}
              </ul>
            ) : (
              <div className="hp-empty-tab">
                <p>
                  {selectedGroup
                    ? `No events or classes listed for ${selectedGroup.name}.`
                    : 'No events or classes found near you.'}
                </p>
              </div>
            )}
          </div>

        </section>

        {/* ── MINYAN PREVIEW ───────────────────────────────────────────── */}
        <section className="hp-mp">
          <div className="hp-mp__inner">

            {/* Head */}
            <div className="hp-mp__head">
              <div>
                <p className="hp-mp__eyebrow">
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    <circle cx="12" cy="9" r="2.5"/>
                  </svg>
                  Find a Minyan
                </p>
                <h2 className="hp-mp__title">Prayer times, near you.</h2>
                <p className="hp-mp__sub">Live minyan times from hundreds of shuls — updated daily.</p>
              </div>
              <button className="hp-mp__all-btn" onClick={() => navigate('/minyan')}>
                See all
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>

            {/* Quick-pick city chips */}
            <div className="hp-mp__chips">
              {QUICK_CITIES.map(c => (
                <button
                  key={c.label}
                  className={`hp-mp__chip${mpCityLabel === c.label ? ' hp-mp__chip--active' : ''}`}
                  onClick={() => mpFetch(c.lat, c.lng, c.label)}
                >
                  {c.label}
                </button>
              ))}
            </div>

            {/* City search */}
            <div className="hp-mp__search-wrap">
              <svg className="hp-mp__search-icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="hp-mp__search"
                type="text"
                placeholder="Search another city…"
                value={mpQuery}
                onChange={e => mpSearchCity(e.target.value)}
                onFocus={() => mpDropItems.length && setMpShowDrop(true)}
                onBlur={() => setTimeout(() => setMpShowDrop(false), 150)}
              />
              {mpShowDrop && mpDropItems.length > 0 && (
                <ul className="hp-mp__drop">
                  {mpDropItems.slice(0, 6).map((item, i) => (
                    <li key={i}>
                      <button className="hp-mp__drop-item" onMouseDown={() => mpPickItem(item)}>
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                          <circle cx="12" cy="9" r="2.5"/>
                        </svg>
                        <span>
                          {item.city || item.name}
                          {item.state ? <em>, {item.state}</em> : null}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Results */}
            {(mpLoading || mpResults.length > 0 || mpCityLabel) ? (
              <div className="hp-mp__results">
                {mpCityLabel && (
                  <p className="hp-mp__results-label">
                    {mpLoading
                      ? `Loading minyanim in ${mpCityLabel}…`
                      : `Minyanim near ${mpCityLabel}`}
                  </p>
                )}

                {mpLoading ? (
                  <ul className="hp-mp__list">
                    {[0,1,2,3].map(i => (
                      <li key={i} className="hp-mp__row hp-mp__row--skeleton">
                        <div className="hp-mp__row-bar" />
                        <div className="hp-mp__skeleton-time" />
                        <div className="hp-mp__skeleton-body" />
                      </li>
                    ))}
                  </ul>
                ) : mpResults.length > 0 ? (
                  <ul className="hp-mp__list">
                    {mpResults.map((item, i) => {
                      const cfg = mpCfg(item.prayer_type);
                      const st  = mpStatus(item.time);
                      return (
                        <li key={item.id || i} className="hp-mp__row">
                          <div className="hp-mp__row-bar" style={{ background: cfg.color }} />
                          <div className="hp-mp__row-time">
                            <span className="hp-mp__time-val">{mpFmt(item.time)}</span>
                            {st && (
                              <span className={`hp-mp__badge hp-mp__badge--${st.type}`}>
                                {st.label}
                              </span>
                            )}
                          </div>
                          <div className="hp-mp__row-body">
                            <span className="hp-mp__prayer-label" style={{ color: cfg.color }}>
                              {cfg.label}
                            </span>
                            <span className="hp-mp__group-name">{item.group_name}</span>
                            <span className="hp-mp__row-meta">
                              {item.city}{item.state ? `, ${item.state}` : ''}
                              {item.distance_miles != null && ` · ${item.distance_miles}mi`}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="hp-mp__empty">No minyanim found for this location.</p>
                )}

                {!mpLoading && mpResults.length > 0 && (
                  <button className="hp-mp__cta" onClick={() => navigate('/minyan')}>
                    Find minyanim near you
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                )}
              </div>
            ) : (
              <div className="hp-mp__idle">
                <p>Select a city above or allow location access to see live minyan times.</p>
                <button className="hp-mp__cta" onClick={() => navigate('/minyan')}>
                  Find minyanim near you
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            )}

          </div>
        </section>

        {/* ── ACTS OF KINDNESS ─────────────────────────────────────────── */}
        {(prayerLoading || prayerGroups.length > 0) && (
          <section className="hp-kindness">
            <div className="hp-kindness__head">
              <div>
                <p className="hp-kindness__eyebrow">Chessed</p>
                <h2 className="hp-kindness__title">Acts of Kindness</h2>
                <p className="hp-kindness__sub">Real people. Real prayers. Join a Tehillim group.</p>
              </div>
              <button className="hp-kindness__start" onClick={() => navigate('/explore-prayers')}>
                Explore more
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </div>

            {prayerLoading ? (
              <ul className="hp-kindness__strip">
                {[...Array(5)].map((_, i) => (
                  <li key={i} className="pk-card pk-card--skel">
                    <div className="pk-card__top" />
                    <div className="pk-card__body">
                      <div className="pk-skel-line" style={{ width: '70%', marginBottom: 6 }} />
                      <div className="pk-skel-line" style={{ width: '50%', marginBottom: 14 }} />
                      <div className="pk-skel-line" style={{ width: '100%', height: 4, marginBottom: 5 }} />
                      <div className="pk-skel-line" style={{ width: '40%', height: 8 }} />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="hp-kindness__strip" ref={kindnessScrollRef}>
                {prayerGroups.map(pg => (
                  <PrayerCard
                    key={pg.id}
                    pg={pg}
                    onClick={() => window.open(`/${pg.short_code}/details`, '_blank')}
                  />
                ))}
                {/* Trailing CTA card */}
                <li className="pk-card pk-card--cta" onClick={openApp}>
                  <div className="pk-card__cta-inner">
                    <div className="pk-card__cta-icon">+</div>
                    <p>Start your own prayer circle</p>
                  </div>
                </li>
              </ul>
            )}
          </section>
        )}

        {/* ── APP CTA ──────────────────────────────────────────────────── */}
        <section className="hp-cta-banner">
          <div className="hp-cta-banner__inner">
            <div>
              <h2 className="hp-cta-banner__heading">Take your community with you</h2>
              <p className="hp-cta-banner__sub">Prayer times, events, classes, giving — all in one place.</p>
            </div>
            <div className="hp-cta-banner__btns">
              <a href="https://apps.apple.com/us/app/yesod/id6748472269"
                target="_blank" rel="noopener noreferrer" className="hp-store-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                App Store
              </a>
              <a href="https://play.google.com/store/apps/details?id=us.yesod.app"
                target="_blank" rel="noopener noreferrer" className="hp-store-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3.18 23.76c.31.17.67.19 1.01.04l12.2-7.05-2.64-2.64-10.57 9.65zM.5 1.27C.19 1.6 0 2.1 0 2.73v18.54c0 .63.19 1.13.5 1.46l.07.07 10.38-10.38v-.24L.57 1.2.5 1.27zM20.34 10.3l-2.91-1.68-2.93 2.93 2.93 2.93 2.93-1.69c.84-.48.84-1.27-.02-1.49zM4.19.24L16.39 7.29l-2.64 2.64L3.18.27C3.52.12 3.88.08 4.19.24z"/></svg>
                Google Play
              </a>
            </div>
          </div>
        </section>

        {/* ── SHABBAT TIMES ────────────────────────────────────────────── */}
        <section
          className="hp-shabbat-cta"
          onClick={() => navigate('/shabbat')}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && navigate('/shabbat')}
        >
          <div className="hp-sc__inner">
            <div className="hp-sc__top">
              <span className="hp-sc__flame" aria-hidden="true">🕯️</span>
              <div className="hp-sc__text">
                <h2 className="hp-sc__heading">Shabbat times for your city</h2>
                <p className={`hp-sc__sub${shabbatData ? ' hp-sc__sub--loaded' : ''}`}>
                  {shabbatLoading
                    ? 'Finding candle lighting near you…'
                    : shabbatData
                      ? (() => {
                          const r = shabbatData.candle_lighting || '';
                          const s = r.trim();
                          const isPM = /pm/i.test(s); const isAM = /am/i.test(s);
                          const c = s.replace(/\s*(am|pm)/gi, '');
                          const [hP='0', mP='0'] = c.split(':');
                          let h = parseInt(hP, 10);
                          const m = String(parseInt(mP,10)||0).padStart(2,'0');
                          if (isPM && h<12) h+=12; if (isAM && h===12) h=0;
                          const ap = h>=12?'PM':'AM'; const h12=h===0?12:h>12?h-12:h;
                          return `This Friday · ${h12}:${m} ${ap}${shabbatData.parsha ? ` · ${shabbatData.parsha}` : ''}`;
                        })()
                      : 'Candle lighting · Havdalah · Weekly parsha — any city'}
                </p>
              </div>
            </div>
            <button
              className="hp-sc__btn"
              onClick={e => { e.stopPropagation(); navigate('/shabbat'); }}
            >
              See my times
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
            <p className="hp-sc__hint">📲 Download the app to get weekly reminders — never look it up again</p>
          </div>
        </section>

      </main>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="hp-footer">
        <div className="hp-footer__inner">
          <span className="hp-footer__brand">© {new Date().getFullYear()} Yesod</span>
          <nav className="hp-footer__links">
            <a href="/privacy-policy">Privacy</a>
            <a href="/terms-of-service">Terms</a>
            <a href="mailto:hello@yesod.us">Contact</a>
            <button
              className="dsm-trigger"
              style={{ fontSize: 12, fontFamily: 'inherit' }}
              onClick={() => setShowDsModal(true)}
            >
              Data Sources
            </button>
          </nav>
        </div>
      </footer>

      <DataSourcesModal isOpen={showDsModal} onClose={() => setShowDsModal(false)} />

    </div>
  );
};

export default HomePage;
